import { AxiosInstance } from 'axios';
import { availableToursEnum } from '@/composables/useUserOnboarding';
import baseAxios from '@/helpers/forms/axios';
import { merchantInterface, MerchantUserRole } from '../merchants';
import { serviceProviderInterface, ServiceProviderUserRole } from '../serviceProviders';

export type UserUuid = string;

export type userOnboardingInterface = Partial<Record<availableToursEnum, number | 'complete' | 'cancelled'>>;

export interface merchantWithRolesInterface extends merchantInterface {
    roles: MerchantUserRole[]
}

export interface serviceProviderWithRolesInterface {
    id: number
    name: string
    url: string
    faviconFile: string
    roles: ServiceProviderUserRole[]
}

export interface userInterface {
    id: number
    uuid: UserUuid
    createdAt: string
    name: string
    email: string
    photoUrl: string
    phone: string
    onboarding?: userOnboardingInterface
}

export interface userWithRolesInterface extends userInterface {
    roles: Array<ServiceProviderUserRole | MerchantUserRole>
}

export interface userMembershipInvitationInterface {
    id: number
    createdAt: string
    email: string
    token: string
}

export interface merchantUserMembershipInvitationInterface extends userMembershipInvitationInterface {
    role: MerchantUserRole
    merchant: merchantInterface
}

export interface serviceProviderUserMembershipInvitationInterface extends userMembershipInvitationInterface {
    role: ServiceProviderUserRole
    serviceProvider: serviceProviderInterface
}

// The endpoint requires at least one of the two, so neither can be optional on its own.
type firstOwnerInvitationUpdateData = {
    email: string
    resend?: boolean
} | {
    email?: string
    resend: true
};

export interface apiTokenInterface {
    id: string
    name: string
    createdAt: string
    lastUsedAt?: string
    token: string
}

export interface createApiTokenFormInterface {
    name: string
}

// Verified public signup: registration collects name + email only. No password
// is set and no session is established until the email is verified.
export interface registerAccountFormInterface {
    name: string
    email: string
}

export interface registerAccountResponseInterface {
    message: string
    email: string
}

export interface verifyRegistrationFormInterface {
    email: string
    token: string
    password: string
    passwordConfirmation: string
}

// Email changes are confirmed out-of-band: this records a pending change and mails a
// confirmation link to the new address. The account keeps its current address until
// that link is opened.
export interface requestEmailChangeFormInterface {
    currentPassword: string
    email: string
}

export interface requestEmailChangeResponseInterface {
    message: string
    email: string
}

export interface verifyEmailChangeFormInterface {
    user: UserUuid
    token: string
}

export interface resetPasswordFormInterface {
    email: string
    token: string
    password: string
    passwordConfirmation: string
}

// ── Onboarding converters ───────────────────────────────────────────────
// The onboarding endpoint returns raw snake_case keys from the database,
// unlike other user endpoints which use Resource classes that return camelCase.

function toOnboarding(r: Record<string, number | 'complete' | 'cancelled'>): userOnboardingInterface {
    return Object.fromEntries(
        Object.entries(r).map(([key, value]) => [
            key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()),
            value,
        ]),
    );
}

function fromOnboarding(o: userOnboardingInterface): Record<string, number | 'complete' | 'cancelled'> {
    const result: Record<string, number | 'complete' | 'cancelled'> = {};
    for (const [key, value] of Object.entries(o)) {
        const snakeKey = key.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);
        result[snakeKey] = value;
    }
    return result;
}

// ── Standalone functions (use baseAxios directly, unaffected by migration) ──

export const fetchApiTokens = async (): Promise<apiTokenInterface[]> => {
    const response = await baseAxios.get<{ data: apiTokenInterface[] }>('/api/users/api-tokens');

    return response.data.data;
};

export const createApiToken = async (data: createApiTokenFormInterface): Promise<apiTokenInterface> => {
    const response = await baseAxios.post<{ data: apiTokenInterface }>('/api/users/api-tokens', { name: data.name });

    return response.data.data;
};

export const deleteApiToken = async (tokenId: string): Promise<void> => {
    return await baseAxios.delete(`/api/users/api-tokens/${tokenId}`);
};

// ── API factory ─────────────────────────────────────────────────────────
// Backend user endpoints return camelCase via Resource classes, so no
// response conversion is needed. Only request body keys that the middleware
// was converting need explicit snake_case (service_provider_id, merchant_id).
// The onboarding endpoint is an exception — it returns raw snake_case.

interface UsersApiReturn {
    registerAccount: (data: registerAccountFormInterface) => Promise<registerAccountResponseInterface>
    verifyRegistration: (data: verifyRegistrationFormInterface) => Promise<void>
    sendPasswordResetLink: (email: string) => Promise<void>
    resetPassword: (data: resetPasswordFormInterface) => Promise<void>
    requestEmailChange: (data: requestEmailChangeFormInterface) => Promise<requestEmailChangeResponseInterface>
    verifyEmailChange: (data: verifyEmailChangeFormInterface) => Promise<void>
    updateOnboarding: (userId: number, onboarding: userOnboardingInterface) => Promise<userOnboardingInterface>
    updatePassword: (currentPassword: string, password: string, passwordConfirmation: string) => Promise<void>
    logoutOtherSessions: (password: string) => Promise<void>
    fetchCurrentUser: () => Promise<userInterface | undefined>
    inviteUserToServiceProvider: (email: string, serviceProviderId: number, role: ServiceProviderUserRole) => Promise<void>
    updateServiceProviderInvitation: (token: string, email: string) => Promise<void>
    acceptServiceProviderInvitation: (token: string) => Promise<void>
    declineServiceProviderInvitation: (token: string) => Promise<void>
    unassignUserRoleForServiceProvider: (userId: number, role: ServiceProviderUserRole) => Promise<void>
    editUserRoleForServiceProvider: (userId: number, role: ServiceProviderUserRole) => Promise<void>
    fetchAccountUsers: () => Promise<userWithRolesInterface[]>
    inviteUserToMerchant: (email: string, merchantId: number, role: MerchantUserRole) => Promise<void>
    updateMerchantInvitation: (token: string, email: string) => Promise<void>
    updateFirstOwnerInvitation: (merchantId: number, data: firstOwnerInvitationUpdateData) => Promise<void>
    resendMerchantInvitation: (token: string) => Promise<void>
    fetchMerchantsForCurrentUser: () => Promise<merchantWithRolesInterface[]>
    fetchMerchantInvitationsForCurrentUser: () => Promise<merchantUserMembershipInvitationInterface[]>
    fetchServiceProvidersForCurrentUser: () => Promise<serviceProviderWithRolesInterface[]>
    fetchServiceProviderInvitationsForCurrentUser: () => Promise<serviceProviderUserMembershipInvitationInterface[]>
    acceptMerchantInvitation: (token: string) => Promise<void>
    declineMerchantInvitation: (token: string) => Promise<void>
    unassignUserRoleForMerchant: (userId: number, role: MerchantUserRole) => Promise<void>
    editUserRoleForMerchant: (userId: number, role: MerchantUserRole) => Promise<void>
    fetchApiTokens: () => Promise<apiTokenInterface[]>
    createApiToken: (data: createApiTokenFormInterface) => Promise<apiTokenInterface>
    deleteApiToken: (tokenId: string) => Promise<void>
    createSignInHandoff: (purpose: SignInHandoffPurpose) => Promise<string>
}

// The destination frontend routes on the purpose it gets back when the handoff is redeemed.
export type SignInHandoffPurpose = 'connect_walmart';

// Hand-written until the create-platform-handoff operation reaches the committed OpenAPI bundle.
interface CreateSignInHandoffRequest {
    purpose: SignInHandoffPurpose
}

interface CreateSignInHandoffResponse {
    token: string
}

export default (axios: AxiosInstance = baseAxios): UsersApiReturn => ({
    registerAccount: async (data: registerAccountFormInterface): Promise<registerAccountResponseInterface> => {
        const response = await axios.post<registerAccountResponseInterface>('/api/register', { name: data.name, email: data.email });

        return response.data;
    },

    verifyRegistration: async (data: verifyRegistrationFormInterface): Promise<void> => {
        await axios.post('/api/register/verify', {
            email: data.email,
            token: data.token,
            password: data.password,
            password_confirmation: data.passwordConfirmation,
        });
    },

    sendPasswordResetLink: async (email: string): Promise<void> => {
        await axios.post('/api/password/email', { email });
    },

    resetPassword: async (data: resetPasswordFormInterface): Promise<void> => {
        await axios.post('/api/password/reset', {
            token: data.token,
            email: data.email,
            password: data.password,
            password_confirmation: data.passwordConfirmation,
        });
    },

    requestEmailChange: async (data: requestEmailChangeFormInterface): Promise<requestEmailChangeResponseInterface> => {
        const response = await axios.post<requestEmailChangeResponseInterface>('/api/users/email', {
            current_password: data.currentPassword,
            email: data.email,
        });

        return response.data;
    },

    verifyEmailChange: async (data: verifyEmailChangeFormInterface): Promise<void> => {
        await axios.post('/api/users/email/verify', {
            user: data.user,
            token: data.token,
        });
    },

    updateOnboarding: async (userId: number, onboarding: userOnboardingInterface): Promise<userOnboardingInterface> => {
        const response = await axios.post<{ onboarding: Record<string, number | 'complete' | 'cancelled'> }>(`/api/users/${userId}/onboarding`, fromOnboarding(onboarding));

        return toOnboarding(response.data.onboarding);
    },

    updatePassword: async (currentPassword: string, password: string, passwordConfirmation: string): Promise<void> => {
        await axios.post('/api/users/password', {
            current_password: currentPassword,
            password,
            password_confirmation: passwordConfirmation,
        });
    },

    logoutOtherSessions: async (password: string): Promise<void> => {
        await axios.post('/api/users/sessions/logout-others', {
            password,
        });
    },

    fetchCurrentUser: async (): Promise<userInterface | undefined> => {
        const response = await axios.get<{ data: userInterface | undefined }>('/api/users/current');

        return response.data.data;
    },

    inviteUserToServiceProvider: async (email: string, serviceProviderId: number, role: ServiceProviderUserRole): Promise<void> => {
        return await axios.post('/api/users/invitations/serviceProvider', {
            email,
            service_provider_id: serviceProviderId,
            role,
        });
    },

    updateServiceProviderInvitation: async (token: string, email: string): Promise<void> => {
        await axios.put(`/api/users/invitations/serviceProvider/${token}`, { email });
    },

    acceptServiceProviderInvitation: async (token: string): Promise<void> => {
        return await axios.get(`/api/users/invitations/serviceProvider/${token}/accept`);
    },

    declineServiceProviderInvitation: async (token: string): Promise<void> => {
        return await axios.get(`/api/users/invitations/serviceProvider/${token}/decline`);
    },

    unassignUserRoleForServiceProvider: async (userId: number, role: ServiceProviderUserRole): Promise<void> => {
        await axios.post(`/api/users/${userId}/roles/serviceProvider/unassign`, {
            role,
        });
    },

    editUserRoleForServiceProvider: async (userId: number, role: ServiceProviderUserRole): Promise<void> => {
        await axios.put(`/api/users/${userId}/roles/serviceProvider`, {
            role,
        });
    },

    fetchAccountUsers: async (): Promise<userWithRolesInterface[]> => {
        const response = await axios.get<userWithRolesInterface[]>('/api/users');
        return response.data;
    },

    inviteUserToMerchant: async (email: string, merchantId: number, role: MerchantUserRole): Promise<void> => {
        return await axios.post('/api/users/invitations/merchant', {
            email,
            merchant_id: merchantId,
            role,
        });
    },

    updateMerchantInvitation: async (token: string, email: string): Promise<void> => {
        await axios.put(`/api/users/invitations/merchant/${token}`, { email });
    },

    updateFirstOwnerInvitation: async (merchantId: number, data: firstOwnerInvitationUpdateData): Promise<void> => {
        await axios.put(`/api/users/invitations/merchant/firstOwner/${merchantId}`, data);
    },

    resendMerchantInvitation: async (token: string): Promise<void> => {
        await axios.get(`/api/users/invitations/merchant/${token}/resend`);
    },

    fetchMerchantsForCurrentUser: async (): Promise<merchantWithRolesInterface[]> => {
        const response = await axios.get<{ merchants: merchantWithRolesInterface[] }>('/api/users/merchants');

        return response.data.merchants;
    },

    fetchMerchantInvitationsForCurrentUser: async (): Promise<merchantUserMembershipInvitationInterface[]> => {
        const response = await axios.get<{ data: merchantUserMembershipInvitationInterface[] }>('/api/users/invitations/merchant');
        return response.data.data;
    },

    fetchServiceProvidersForCurrentUser: async (): Promise<serviceProviderWithRolesInterface[]> => {
        const response = await axios.get<{ service_providers: serviceProviderWithRolesInterface[] }>('/api/users/serviceProviders');

        return response.data.service_providers;
    },

    fetchServiceProviderInvitationsForCurrentUser: async (): Promise<serviceProviderUserMembershipInvitationInterface[]> => {
        const response = await axios.get<{ data: serviceProviderUserMembershipInvitationInterface[] }>('/api/users/invitations/serviceProvider');
        return response.data.data;
    },

    acceptMerchantInvitation: async (token: string): Promise<void> => {
        return await axios.get(`/api/users/invitations/merchant/${token}/accept`);
    },

    declineMerchantInvitation: async (token: string): Promise<void> => {
        return await axios.get(`/api/users/invitations/merchant/${token}/decline`);
    },

    unassignUserRoleForMerchant: async (userId: number, role: MerchantUserRole): Promise<void> => {
        await axios.post(`/api/users/${userId}/roles/merchant/unassign`, {
            role,
        });
    },

    editUserRoleForMerchant: async (userId: number, role: MerchantUserRole): Promise<void> => {
        await axios.put(`/api/users/${userId}/roles/merchant`, {
            role,
        });
    },

    fetchApiTokens: async (): Promise<apiTokenInterface[]> => {
        const response = await axios.get<{ data: apiTokenInterface[] }>('/api/users/api-tokens');

        return response.data.data;
    },

    createApiToken: async (data: createApiTokenFormInterface): Promise<apiTokenInterface> => {
        const response = await axios.post<{ data: apiTokenInterface }>('/api/users/api-tokens', { name: data.name });

        return response.data.data;
    },

    deleteApiToken: async (tokenId: string): Promise<void> => {
        return await axios.delete(`/api/users/api-tokens/${tokenId}`);
    },

    createSignInHandoff: async (purpose: SignInHandoffPurpose): Promise<string> => {
        const body: CreateSignInHandoffRequest = { purpose };
        const response = await axios.post<CreateSignInHandoffResponse>('/api/platform/handoffs', body);

        return response.data.token;
    },
});
